(() => {
  const runtimeKey = '__keepBalanceEternalRuntime';
  if (window[runtimeKey]) {
    window[runtimeKey].initializeRoots(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const activeRoots = new Map();
  const keyHandlers = new Set();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cartRuntime = { drawer: null, closeButtons: [], closeHandler: null, clickHandler: null, setCart: null, changeCart: null };
  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    const rootKeyHandlers = [];
    const registerKeyHandler = (handler) => {
      keyHandlers.add(handler);
      rootKeyHandlers.push(handler);
    };
    let newsletterTimer = null;
    let countdownTimer = null;

  const body = document.body;
  const menu = root.querySelector('[data-eternal-menu]');
  const scrim = root.querySelector('[data-eternal-scrim]');
  const search = root.querySelector('[data-eternal-search]');
  const submenu = root.querySelector('[data-eternal-submenu]');
  const submenuToggle = root.querySelector('[data-eternal-submenu-toggle]');
  const cartDrawer = document.querySelector('[data-eternal-cart-drawer]');
  const newsletter = root.querySelector('[data-eternal-newsletter-modal]');
  const newsletterOpenState = root.querySelector('[data-eternal-newsletter-open]') || newsletter;
  const newsletterDismissKey = 'keepbalance-newsletter-dismissed';
  const menuTriggers = Array.from(root.querySelectorAll('[data-eternal-menu-open]'));
  const searchTriggers = Array.from(root.querySelectorAll('[data-eternal-search-open]'));
  const cartTriggers = Array.from(root.querySelectorAll('[data-eternal-cart-open]'));
  const focusableSelector = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';
  let menuReturnFocus = null;
  let searchReturnFocus = null;
  let cartReturnFocus = null;
  let newsletterReturnFocus = null;

  const lock = (enabled) => body.classList.toggle('eternal-lock', enabled);
  const getFocusable = (surface) => Array.from(surface?.querySelectorAll(focusableSelector) || []).filter((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return !element.closest('[inert]') && style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  });
  const restoreFocus = (target) => {
    if (!(target instanceof HTMLElement) || !target.isConnected || target.closest('[inert]')) return;
    target.focus({ preventScroll: true });
  };
  const setExpanded = (triggers, expanded) => triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  const setSurfaceState = (surface, triggers, open) => {
    if (!surface) return;
    surface.classList.toggle('is-open', open);
    surface.setAttribute('aria-hidden', String(!open));
    surface.toggleAttribute('inert', !open);
    setExpanded(triggers, open);
  };
  const syncLock = () => lock(Boolean(newsletter && !newsletter.hidden) || [menu, search, cartDrawer].some((surface) => surface?.classList.contains('is-open')));
  const focusInside = (surface, preferred) => {
    window.requestAnimationFrame(() => {
      const target = preferred || getFocusable(surface)[0] || surface;
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    });
  };
  const setMenu = (open, { trigger = null, restore = true } = {}) => {
    if (!menu) return;
    const wasOpen = menu.classList.contains('is-open');
    if (open) {
      setSearch(false, { restore: false });
      setCart(false, { restore: false });
      menuReturnFocus = trigger || document.activeElement;
    }
    setSurfaceState(menu, menuTriggers, open);
    scrim?.classList.toggle('is-open', open);
    scrim?.setAttribute('aria-hidden', String(!open));
    syncLock();
    if (open) focusInside(menu);
    if (!open && wasOpen) {
      if (restore) restoreFocus(menuReturnFocus);
      menuReturnFocus = null;
    }
  };
  const setSearch = (open, { trigger = null, restore = true } = {}) => {
    if (!search) return;
    const wasOpen = search.classList.contains('is-open');
    if (open) {
      setMenu(false, { restore: false });
      setCart(false, { restore: false });
      searchReturnFocus = trigger || document.activeElement;
    }
    setSurfaceState(search, searchTriggers, open);
    syncLock();
    if (open) focusInside(search, search.querySelector('[data-eternal-search-input]'));
    if (!open && wasOpen) {
      if (restore) restoreFocus(searchReturnFocus);
      searchReturnFocus = null;
    }
  };
  const setCart = (open, { trigger = null, restore = true } = {}) => {
    if (!cartDrawer) return;
    const wasOpen = cartDrawer.classList.contains('is-open');
    if (open) {
      setMenu(false, { restore: false });
      setSearch(false, { restore: false });
      cartReturnFocus = trigger || document.activeElement;
    }
    setSurfaceState(cartDrawer, cartTriggers, open);
    syncLock();
    if (open) focusInside(cartDrawer);
    if (!open && wasOpen) {
      if (restore) restoreFocus(cartReturnFocus);
      cartReturnFocus = null;
    }
  };
  const newsletterWasDismissed = () => {
    try {
      return sessionStorage.getItem(newsletterDismissKey) === 'true';
    } catch (_) {
      return false;
    }
  };
  const dismissNewsletterForSession = () => {
    try {
      sessionStorage.setItem(newsletterDismissKey, 'true');
    } catch (_) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  };
  const setNewsletter = (open, { restore = true } = {}) => {
    if (!newsletter) return;
    const wasOpen = !newsletter.hidden;
    if (open) {
      setMenu(false, { restore: false });
      setSearch(false, { restore: false });
      setCart(false, { restore: false });
      newsletterReturnFocus = document.activeElement;
    }
    newsletter.hidden = !open;
    if (newsletterOpenState) newsletterOpenState.dataset.eternalNewsletterOpen = String(open);
    newsletter.setAttribute('aria-hidden', String(!open));
    syncLock();
    if (open) focusInside(newsletter, newsletter.querySelector('input[name="contact[email]"]'));
    if (!open && wasOpen) {
      dismissNewsletterForSession();
      if (restore) restoreFocus(newsletterReturnFocus);
      newsletterReturnFocus = null;
    }
  };

  menuTriggers.forEach((button) => button.addEventListener('click', () => setMenu(true, { trigger: button })));
  root.querySelector('[data-eternal-menu-close]')?.addEventListener('click', () => setMenu(false));
  scrim?.addEventListener('click', () => setMenu(false));
  searchTriggers.forEach((button) => {
    button.addEventListener('click', () => {
      const returnTarget = button.closest('[data-eternal-menu]') ? menuReturnFocus || menuTriggers[0] : button;
      setMenu(false, { restore: false });
      setSearch(true, { trigger: returnTarget });
    });
  });
  root.querySelector('[data-eternal-search-close]')?.addEventListener('click', () => setSearch(false));
  search?.addEventListener('click', (event) => {
    if (event.target === search) setSearch(false);
  });
  cartTriggers.forEach((button) => button.addEventListener('click', () => setCart(true, { trigger: button })));
  newsletter?.querySelectorAll('[data-eternal-newsletter-close]').forEach((button) => button.addEventListener('click', () => setNewsletter(false)));
  newsletter?.addEventListener('click', (event) => {
    if (event.target === newsletter) setNewsletter(false);
  });
  if (newsletter?.querySelector('[data-eternal-newsletter-success]')) {
    dismissNewsletterForSession();
  } else if (newsletter && !newsletterWasDismissed()) {
    newsletterTimer = window.setTimeout(() => setNewsletter(true), 350);
  }

  submenuToggle?.addEventListener('click', () => {
    const open = !submenu?.classList.contains('is-open');
    submenu?.classList.toggle('is-open', open);
    submenuToggle.setAttribute('aria-expanded', String(open));
    const marker = submenuToggle.querySelector('span');
    if (marker) marker.textContent = open ? '−' : '+';
  });

  registerKeyHandler((event) => {
    const activeSurface = newsletter && !newsletter.hidden ? newsletter : [cartDrawer, search, menu].find((surface) => surface?.classList.contains('is-open'));
    if (!activeSurface) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (activeSurface === newsletter) setNewsletter(false);
      if (activeSurface === cartDrawer) setCart(false);
      if (activeSurface === search) setSearch(false);
      if (activeSurface === menu) setMenu(false);
      return true;
    }
    if (event.key !== 'Tab') return false;
    const focusable = getFocusable(activeSurface);
    if (!focusable.length) {
      event.preventDefault();
      activeSurface.focus({ preventScroll: true });
      return true;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !activeSurface.contains(active))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (active === last || !activeSurface.contains(active))) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
    return true;
  });

  const revealItems = root.querySelectorAll('.eternal-reveal');
  if ('IntersectionObserver' in window && !prefersReducedMotion.matches) {
    const revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }),
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    revealItems.forEach((item) => revealObserver.observe(item));
  } else revealItems.forEach((item) => item.classList.add('is-visible'));

  root.querySelectorAll('[data-eternal-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-eternal-track]');
    const move = (direction) => {
      const card = track?.querySelector(':scope > *');
      if (!track || !card) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap || '16');
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    };
    carousel.querySelector('[data-eternal-prev]')?.addEventListener('click', () => move(-1));
    carousel.querySelector('[data-eternal-next]')?.addEventListener('click', () => move(1));
  });

  const story = root.querySelector('[data-eternal-team-stage]');
  if (story) {
    const images = (story.dataset.eternalStoryImages || '').split('|').filter(Boolean);
    const labels = (story.dataset.eternalStoryLabels || '').split('|');
    const slots = {
      previous: story.querySelector('[data-eternal-story-prev-slot] img'),
      current: story.querySelector('[data-eternal-story-main-slot] img'),
      next: story.querySelector('[data-eternal-story-next-slot] img')
    };
    const cta = story.querySelector('[data-eternal-story-cta]');
    let storyIndex = Number(story.dataset.eternalStoryIndex || 0);
    const showStory = (index) => {
      if (!images.length) return;
      storyIndex = (index + images.length) % images.length;
      const previousIndex = (storyIndex - 1 + images.length) % images.length;
      const nextIndex = (storyIndex + 1) % images.length;
      if (slots.previous) slots.previous.src = images[previousIndex];
      if (slots.current) slots.current.src = images[storyIndex];
      if (slots.next) slots.next.src = images[nextIndex];
      if (cta) cta.textContent = labels[storyIndex] || 'EXPLORE';
      story.dataset.eternalStoryIndex = String(storyIndex);
    };
    story.querySelector('[data-eternal-story-prev]')?.addEventListener('click', () => showStory(storyIndex - 1));
    story.querySelector('[data-eternal-story-next]')?.addEventListener('click', () => showStory(storyIndex + 1));
  }

  root.querySelectorAll('[data-eternal-ingredient]').forEach((button) => {
    button.addEventListener('click', () => {
      const panelId = button.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      button.classList.toggle('is-active', !expanded);
      if (panel) panel.hidden = expanded;
    });
  });

  let testimonialIndex = 0;
  const testimonialRail = root.querySelector('[data-eternal-testimonial-rail]');
  const testimonialCounter = root.querySelector('[data-eternal-testimonial-index]');
  const testimonialTotal = testimonialRail?.children.length || 0;
  const showTestimonial = (index) => {
    if (!testimonialRail || !testimonialTotal) return;
    testimonialIndex = (index + testimonialTotal) % testimonialTotal;
    testimonialRail.style.transform = `translateX(-${testimonialIndex * 100}%)`;
    if (testimonialCounter) testimonialCounter.textContent = `${String(testimonialIndex + 1).padStart(2, '0')} / ${String(testimonialTotal).padStart(2, '0')}`;
  };
  root.querySelector('[data-eternal-testimonial-prev]')?.addEventListener('click', () => showTestimonial(testimonialIndex - 1));
  root.querySelector('[data-eternal-testimonial-next]')?.addEventListener('click', () => showTestimonial(testimonialIndex + 1));

  root.querySelectorAll('.eternal-faq details').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      root.querySelectorAll('.eternal-faq details').forEach((other) => {
        if (other !== details) other.open = false;
      });
    });
  });

  const countdown = root.querySelector('[data-eternal-countdown]');
  if (countdown) {
    const end = Date.parse(countdown.dataset.end || '');
    const renderCountdown = (remaining) => {
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining / 3600000) % 24);
      const minutes = Math.floor((remaining / 60000) % 60);
      const seconds = Math.floor((remaining / 1000) % 60);
      const values = { days, hours, minutes, seconds };
      Object.entries(values).forEach(([key, value]) => {
        const slot = countdown.querySelector(`[data-${key}]`);
        if (slot) slot.textContent = String(value).padStart(2, '0');
      });
    };
    const updateCountdown = () => {
      const remaining = Math.max(0, end - Date.now());
      renderCountdown(remaining);
      if (remaining === 0) {
        countdown.dataset.countdownComplete = 'true';
        if (countdownTimer !== null) {
          window.clearInterval(countdownTimer);
          countdownTimer = null;
        }
        return true;
      }
      return false;
    };
    if (!Number.isFinite(end)) {
      renderCountdown(0);
      countdown.dataset.countdownComplete = 'true';
    } else if (!updateCountdown()) {
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  }

  root.querySelectorAll('[data-eternal-video]').forEach((button) => {
    button.addEventListener('click', () => {
      const video = button.previousElementSibling;
      if (!(video instanceof HTMLVideoElement)) return;
      if (video.paused) {
        video.play();
        button.textContent = 'PAUSE';
      } else {
        video.pause();
        button.textContent = 'PLAY';
      }
    });
  });

  const product = root.matches('[data-eternal-product]') ? root : root.querySelector('[data-eternal-product]');
  if (product) {
    const productZoom = product.querySelector('[data-eternal-product-zoom]');
    const productLightbox = product.querySelector('[data-eternal-product-lightbox]');
    const productLightboxClose = productLightbox?.querySelector('[data-eternal-product-lightbox-close]');
    const setProductLightbox = (open) => {
      if (!productLightbox) return;
      productLightbox.hidden = !open;
      productLightbox.setAttribute('aria-hidden', String(!open));
      lock(open);
      if (open) window.setTimeout(() => productLightboxClose?.focus(), 0);
      else productZoom?.focus();
    };
    productZoom?.addEventListener('click', () => setProductLightbox(true));
    productLightboxClose?.addEventListener('click', () => setProductLightbox(false));
    productLightbox?.addEventListener('click', (event) => {
      if (event.target === productLightbox) setProductLightbox(false);
    });
    registerKeyHandler((event) => {
      if (event.key !== 'Escape' || productLightbox?.hidden) return false;
      setProductLightbox(false);
      return true;
    });

    product.querySelector('[data-eternal-product-form]')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      const submit = form.querySelector('[type="submit"]');
      submit?.setAttribute('disabled', 'disabled');
      try {
        if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
          const previewProduct = window.KeepBalancePreviewProduct || { key: 'preview', product_title: 'KeepBalance product', vendor: 'KeepBalance', price: 0, image: '' };
          const requestedQuantity = Math.max(1, Number(new FormData(form).get('quantity') || 1));
          const existing = previewCart.items.find((item) => item.key === previewProduct.key);
          const items = existing
            ? previewCart.items.map((item) => item.key === previewProduct.key ? { ...item, quantity: item.quantity + requestedQuantity } : item)
            : [...previewCart.items, { ...previewProduct, quantity: requestedQuantity }];
          setPreviewCart(items);
          setCart(true);
          return;
        }
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        await refreshCart();
        setCart(true);
      } finally {
        submit?.removeAttribute('disabled');
      }
    });
    const mediaRail = product.querySelector('[data-eternal-product-media-rail]');
    const mediaItems = Array.from(product.querySelectorAll('[data-eternal-product-media]'));
    const mediaIndex = product.querySelector('[data-eternal-product-index]');
    let currentMedia = 0;
    const showMedia = (index) => {
      if (!mediaRail || !mediaItems.length) return;
      currentMedia = (index + mediaItems.length) % mediaItems.length;
      mediaRail.scrollTo({ left: mediaItems[currentMedia].offsetLeft, behavior: 'smooth' });
      if (mediaIndex) mediaIndex.textContent = String(currentMedia + 1).padStart(2, '0');
    };
    product.querySelector('[data-eternal-product-prev]')?.addEventListener('click', () => showMedia(currentMedia - 1));
    product.querySelector('[data-eternal-product-next]')?.addEventListener('click', () => showMedia(currentMedia + 1));
    mediaRail?.addEventListener('scroll', () => {
      const width = mediaRail.clientWidth || 1;
      currentMedia = Math.max(0, Math.min(mediaItems.length - 1, Math.round(mediaRail.scrollLeft / width)));
      if (mediaIndex) mediaIndex.textContent = String(currentMedia + 1).padStart(2, '0');
    }, { passive: true });

    const quantity = product.querySelector('[data-eternal-qty]');
    product.querySelector('[data-eternal-qty-minus]')?.addEventListener('click', () => {
      if (quantity) quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1));
    });
    product.querySelector('[data-eternal-qty-plus]')?.addEventListener('click', () => {
      if (quantity) quantity.value = String(Math.max(1, Number(quantity.value || 1) + 1));
    });

    product.querySelector('[data-eternal-variant-select]')?.addEventListener('change', (event) => {
      const selected = event.currentTarget.selectedOptions[0];
      const price = Number(selected?.dataset.price || 0);
      const priceNode = product.querySelector('[data-product-price]');
      if (priceNode && price) priceNode.textContent = money(price);
      const compareNode = product.querySelector('.eternal-product-compare-price');
      if (compareNode) {
        const compareAtPrice = Number(selected?.dataset.compareAtPrice || 0);
        if (compareAtPrice > price) {
          compareNode.hidden = false;
          const compareValue = compareNode.querySelector('s');
          if (compareValue) compareValue.textContent = money(compareAtPrice);
        } else compareNode.hidden = true;
      }
      const available = selected?.dataset.available !== 'false';
      const inventoryNode = product.querySelector('[data-product-inventory]');
      if (inventoryNode) inventoryNode.textContent = available ? 'Available' : 'Currently unavailable';
      const submit = product.querySelector('[type="submit"][name="add"]');
      if (submit) {
        submit.disabled = !available;
        submit.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }
      const stickyPrice = product.querySelector('[data-eternal-sticky-price]');
      if (stickyPrice && price) stickyPrice.textContent = money(price);
      const stickyAdd = product.querySelector('[data-eternal-sticky-add]');
      if (stickyAdd) {
        stickyAdd.disabled = !available;
        stickyAdd.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }
    });

    product.querySelectorAll('[data-eternal-product-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const key = tab.dataset.eternalProductTab;
        product.querySelectorAll('[data-eternal-product-tab]').forEach((item) => item.classList.toggle('is-active', item === tab));
        product.querySelectorAll('[data-eternal-product-panel]').forEach((panel) => panel.classList.toggle('is-active', panel.dataset.eternalProductPanel === key));
      });
    });

    const stickybar = product.querySelector('[data-eternal-product-stickybar]');
    const sourcePanel = product.querySelector('.eternal-product-source');
    const secondaryProductSection = root.querySelector('[data-eternal-secondary-product]');
    let primaryStickyPurchaseLocked = false;
    if (stickybar && sourcePanel && 'IntersectionObserver' in window) {
      stickybar.hidden = false;
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => stickybar.classList.toggle('is-visible', !primaryStickyPurchaseLocked && !entry.isIntersecting && entry.boundingClientRect.top < 0));
      }, { threshold: 0 }).observe(sourcePanel);
      if (secondaryProductSection) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return;
            primaryStickyPurchaseLocked = true;
            stickybar.classList.remove('is-visible');
          });
        }, { threshold: 0 }).observe(secondaryProductSection);
      }
    }
    product.querySelector('[data-eternal-sticky-add]')?.addEventListener('click', () => {
      product.querySelector('[data-eternal-product-form]')?.requestSubmit();
    });
  }

  root.querySelectorAll('[data-eternal-secondary-product]').forEach((secondaryProduct) => {
    const secondaryZoom = secondaryProduct.querySelector('[data-eternal-secondary-zoom]');
    const secondaryLightbox = secondaryProduct.querySelector('[data-eternal-secondary-lightbox]');
    const secondaryLightboxClose = secondaryProduct.querySelector('[data-eternal-secondary-lightbox-close]');
    const setSecondaryLightbox = (open) => {
      if (!secondaryLightbox) return;
      secondaryLightbox.hidden = !open;
      secondaryLightbox.setAttribute('aria-hidden', String(!open));
      lock(open);
      if (open) window.setTimeout(() => secondaryLightboxClose?.focus(), 0);
      else secondaryZoom?.focus();
    };
    secondaryZoom?.addEventListener('click', () => setSecondaryLightbox(true));
    secondaryLightboxClose?.addEventListener('click', () => setSecondaryLightbox(false));
    secondaryLightbox?.addEventListener('click', (event) => {
      if (event.target === secondaryLightbox) setSecondaryLightbox(false);
    });
    registerKeyHandler((event) => {
      if (event.key !== 'Escape' || secondaryLightbox?.hidden) return false;
      setSecondaryLightbox(false);
      return true;
    });

    const secondaryForm = secondaryProduct.querySelector('[data-eternal-secondary-product-form]');
    const secondaryQuantity = secondaryProduct.querySelector('[data-eternal-secondary-qty]');
    const secondaryPreviewKey = secondaryProduct.dataset.eternalPreviewProductKey;
    const updateSecondaryPreviewProduct = () => {
      if (!secondaryPreviewKey || !window.KeepBalancePreviewProducts?.[secondaryPreviewKey]) return;
      const selectedVariant = secondaryProduct.querySelector('[data-eternal-secondary-variant-select]')?.selectedOptions[0];
      const variantId = selectedVariant?.value || new FormData(secondaryForm || undefined).get('id');
      const price = Number(selectedVariant?.dataset.price || window.KeepBalancePreviewProducts[secondaryPreviewKey].price || 0);
      const variantTitle = selectedVariant?.textContent?.split(' — ')[0] || 'Default Title';
      window.KeepBalancePreviewProducts[secondaryPreviewKey] = {
        ...window.KeepBalancePreviewProducts[secondaryPreviewKey],
        key: `${secondaryPreviewKey}-${variantId}`,
        price,
        final_price: price,
        variant_id: variantId,
        variant_title: variantTitle
      };
    };
    secondaryProduct.querySelector('[data-eternal-secondary-qty-minus]')?.addEventListener('click', () => {
      if (secondaryQuantity) secondaryQuantity.value = String(Math.max(1, Number(secondaryQuantity.value || 1) - 1));
    });
    secondaryProduct.querySelector('[data-eternal-secondary-qty-plus]')?.addEventListener('click', () => {
      if (secondaryQuantity) secondaryQuantity.value = String(Math.max(1, Number(secondaryQuantity.value || 1) + 1));
    });
    secondaryProduct.querySelector('[data-eternal-secondary-variant-select]')?.addEventListener('change', (event) => {
      const selected = event.currentTarget.selectedOptions[0];
      const price = Number(selected?.dataset.price || 0);
      const available = selected?.dataset.available !== 'false';
      const priceNode = secondaryProduct.querySelector('[data-eternal-secondary-product-price]');
      const inventoryNode = secondaryProduct.querySelector('[data-eternal-secondary-product-inventory]');
      const submit = secondaryForm?.querySelector('[name="add"]');
      if (priceNode && price) priceNode.textContent = money(price);
      if (inventoryNode) inventoryNode.textContent = available ? 'Available' : 'Currently unavailable';
      if (submit) {
        submit.disabled = !available;
        submit.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }
      updateSecondaryPreviewProduct();
    });
    updateSecondaryPreviewProduct();
    secondaryForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      const submit = form.querySelector('[type="submit"]');
      submit?.setAttribute('disabled', 'disabled');
      try {
        if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
          const previewProduct = window.KeepBalancePreviewProducts?.[secondaryPreviewKey] || window.KeepBalancePreviewProduct;
          if (!previewProduct) return;
          const requestedQuantity = Math.max(1, Number(new FormData(form).get('quantity') || 1));
          const existing = previewCart.items.find((item) => item.key === previewProduct.key);
          const items = existing
            ? previewCart.items.map((item) => item.key === previewProduct.key ? { ...item, quantity: item.quantity + requestedQuantity } : item)
            : [...previewCart.items, { ...previewProduct, quantity: requestedQuantity }];
          setPreviewCart(items);
          setCart(true);
          return;
        }
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        await refreshCart();
        setCart(true);
      } finally {
        submit?.removeAttribute('disabled');
      }
    });
  });

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format((cents || 0) / 100);
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  const cartFromItems = (items) => {
    const normalizedItems = items.map((item) => {
      const quantity = Math.max(0, Number(item.quantity || 0));
      const unitPrice = Number(item.final_price ?? item.price ?? (quantity ? Number(item.final_line_price || 0) / quantity : 0));
      return { ...item, quantity, final_price: unitPrice, final_line_price: unitPrice * quantity };
    }).filter((item) => item.quantity > 0);
    return {
      items: normalizedItems,
      item_count: normalizedItems.reduce((total, item) => total + item.quantity, 0),
      total_price: normalizedItems.reduce((total, item) => total + item.final_line_price, 0)
    };
  };
  let previewCart = cartFromItems([]);
  const renderCart = (cart) => {
    if (!cartDrawer) return;
    const items = cart.items || [];
    const hasItems = items.length > 0;
    cartDrawer.querySelector('[data-eternal-cart-empty]')?.toggleAttribute('hidden', hasItems);
    cartDrawer.querySelector('[data-eternal-cart-filled]')?.toggleAttribute('hidden', !hasItems);
    cartDrawer.querySelector('[data-eternal-cart-footer]')?.toggleAttribute('hidden', !hasItems);
    document.querySelectorAll('[data-eternal-cart-count]').forEach((count) => { count.textContent = String(cart.item_count || 0); });
    const lines = cartDrawer.querySelector('[data-eternal-cart-lines]');
    if (lines) lines.innerHTML = items.map((item) => {
      const title = item.product_title || item.title || item.product?.title || 'Product';
      const vendor = item.vendor || item.product?.vendor || 'KeepBalance';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<span class="eternal-cart-drawer__variant">${escapeHtml(item.variant_title)}</span>` : '';
      const rawImage = typeof item.image === 'string' ? item.image : item.image?.url || item.featured_image?.url || '';
      const image = rawImage ? `<img src="${escapeHtml(rawImage)}" width="90" height="90" alt="${escapeHtml(title)}" loading="lazy">` : '<div class="eternal-cart-drawer__image-placeholder" aria-hidden="true"></div>';
      return `<div class="eternal-cart-drawer__line" data-eternal-cart-line data-cart-key="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity}">
        ${image}<div><small>${escapeHtml(vendor)}</small><strong data-eternal-cart-title>${escapeHtml(title)}</strong>${variant}<div class="eternal-cart-drawer__quantity"><button type="button" data-eternal-cart-minus aria-label="Decrease ${escapeHtml(title)} quantity">−</button><span data-eternal-cart-quantity>${item.quantity}</span><button type="button" data-eternal-cart-plus aria-label="Increase ${escapeHtml(title)} quantity">+</button></div></div>
        <div class="eternal-cart-drawer__line-end"><span data-eternal-cart-line-price>${money(item.final_line_price)}</span><button type="button" data-eternal-cart-remove aria-label="Remove ${escapeHtml(title)}">REMOVE</button></div></div>`;
    }).join('');
    const subtotal = cartDrawer.querySelector('[data-eternal-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);
  };
  const setPreviewCart = (items) => {
    previewCart = cartFromItems(items);
    renderCart(previewCart);
    return previewCart;
  };
  const refreshCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };
  const changeCart = async (key, quantity) => {
    if (!key) return;
    if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
      setPreviewCart(previewCart.items.map((item) => item.key === key ? { ...item, quantity } : item));
      return;
    }
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    renderCart(await response.json());
  };
  const registerCartRuntime = () => {
    if (!cartDrawer) return;
    cartRuntime.setCart = setCart;
    cartRuntime.changeCart = changeCart;
    if (cartRuntime.drawer === cartDrawer) return;
    cartRuntime.closeButtons.forEach((button) => button.removeEventListener('click', cartRuntime.closeHandler));
    cartRuntime.drawer?.removeEventListener('click', cartRuntime.clickHandler);
    cartRuntime.drawer = cartDrawer;
    cartRuntime.closeHandler = () => cartRuntime.setCart?.(false);
    cartRuntime.closeButtons = Array.from(cartDrawer.querySelectorAll('[data-eternal-cart-close]'));
    cartRuntime.closeButtons.forEach((button) => button.addEventListener('click', cartRuntime.closeHandler));
    cartRuntime.clickHandler = (event) => {
      const action = event.target.closest('[data-eternal-cart-minus],[data-eternal-cart-plus],[data-eternal-cart-remove]');
      if (!action) return;
      const line = action.closest('[data-eternal-cart-line]');
      if (!line?.dataset.cartKey) return;
      const currentQuantity = Number(line.dataset.cartQuantity || 0);
      const nextQuantity = action.hasAttribute('data-eternal-cart-remove') ? 0 : action.hasAttribute('data-eternal-cart-plus') ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);
      cartRuntime.changeCart?.(line.dataset.cartKey, nextQuantity);
    };
    cartDrawer.addEventListener('click', cartRuntime.clickHandler);
  };
  registerCartRuntime();
  window.KeepBalanceCart = { renderCart, setPreviewCart, open: () => setCart(true), close: () => setCart(false) };
    const cleanup = () => {
      if (newsletterTimer !== null) window.clearTimeout(newsletterTimer);
      if (countdownTimer !== null) window.clearInterval(countdownTimer);
      rootKeyHandlers.forEach((handler) => keyHandlers.delete(handler));
      activeRoots.delete(root);
    };
    activeRoots.set(root, cleanup);
  };

  const pruneDisconnectedRoots = () => {
    activeRoots.forEach((cleanup, root) => {
      if (!root.isConnected) cleanup();
    });
  };
  const initializeRoots = (scope = document) => {
    const roots = [];
    if (scope instanceof HTMLElement && scope.matches('[data-eternal-root]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-eternal-root]'));
    roots
      .filter((root) => !root.parentElement?.closest('[data-eternal-root]'))
      .forEach((root) => initializeRoot(root));
  };
  const handleKeydown = (event) => {
    pruneDisconnectedRoots();
    Array.from(keyHandlers).reverse().some((handler) => handler(event));
  };

  window[runtimeKey] = { initializeRoots };
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('shopify:section:load', (event) => {
    pruneDisconnectedRoots();
    initializeRoots(event.target);
  });
  initializeRoots();
})();
